"use client";

import { useState, useEffect, FC } from "react";
import "react-responsive-carousel/lib/styles/carousel.min.css"; // requires a loader
import { Carousel } from "react-responsive-carousel";
import cx from "classnames";
import styles from "./gallery.module.scss";
import "./carousel.scss";
import CarCardImageButton, { ArrowDirection } from "../../../ui/common-components/vehicle-card/car-card-image-button";
import Iframe from "../../../ui/common-components/iframe";
import { Vehicle } from "../../../types/vehicle";
import { getTranslationModule, Translations } from "../../../utils/translations";
import { VDPBlockGlobalInitData } from "@/types/vdp";
import { useIsMobile } from "@/utils/useIsMobile";
import Discount from "@/ui/common-components/vehicle-card/discount/discount";

interface IGalleryTabInfo {
  title: string;
  iframeUrl: string;
}

interface IGallery {
  images: string[];
  smallImages: string[];
  dealerPhoto?: string;
  currentVehicle: Vehicle;
  customGalleriaItemTabs?: IGalleryTabInfo[];
  translations: Translations;
  vdpGlobalData?: VDPBlockGlobalInitData;
  isSmartphone?: boolean;
}

const SCROLL_TOLLERANCE = 30;

const Gallery: FC<IGallery> = ({
  images,
  smallImages,
  dealerPhoto,
  currentVehicle,
  customGalleriaItemTabs,
  translations,
  vdpGlobalData,
  isSmartphone,
}) => {
  const t_common = getTranslationModule(translations, "common");
  const t_vdp = getTranslationModule(translations, "vdp");
  const [swipeEnabled, setSwipeEnabled] = useState(true);
  const [touchEventsSet, setTouchEventsSet] = useState(false);
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [selectedItem, setSelectedItem] = useState(0);
  const [currentVehicleId, setCurrentVehicleId] = useState(currentVehicle.id);
  const isMobile = useIsMobile(768, isSmartphone);

  const handleOnImageLoadError = (e: any, item: Vehicle) => {
    e.target.src = `/images/brands/${item?.make?.toLowerCase()}.png` || "/images/out-of-stock.png";
  };

  useEffect(() => {
    if (currentVehicle.id !== currentVehicleId) {
      setSelectedItem(0);
      setCurrentVehicleId(currentVehicle.id);
    }
  }, [currentVehicle.id, currentVehicle, currentVehicleId]);

  useEffect(() => {
    if (images?.length) {
      const disableScroll = () => {
        let carousel = document.querySelector(".vehiclesPageCarousel");

        let startPos: any;
        let endPos;
        let isScrolling;
        let passedSwipeScrollTolerance = false;

        function touchMove(e: any) {
          if (e.targetTouches.length > 1 || (e.scale && e.scale !== 1) || !startPos) {
            return;
          }

          if (!passedSwipeScrollTolerance && swipeEnabled) {
            const touch = e.targetTouches[0];
            endPos = {
              x: Math.abs(touch.pageX - startPos.x),
              y: Math.abs(touch.pageY - startPos.y),
            };
            passedSwipeScrollTolerance = endPos.x > SCROLL_TOLLERANCE;

            isScrolling = endPos.x < endPos.y; //When isScrolling it means vertical sliding
            if (isScrolling) {
              setSwipeEnabled(false);
            }
          }
        }

        function touchEnd() {
          setSwipeEnabled(true);
          passedSwipeScrollTolerance = false;
          carousel && carousel.removeEventListener("touchmove", touchMove);
        }

        function touchStart(e: any) {
          const touch = e.targetTouches[0]; //The touches array object gets all the touches on the screen, taking the first touch
          startPos = {
            x: touch.pageX,
            y: touch.pageY,
          };

          carousel && carousel.addEventListener("touchmove", touchMove);
        }

        if (!touchEventsSet) {
          carousel && carousel.addEventListener("touchstart", touchStart);
          carousel && carousel.addEventListener("touchend", touchEnd);
          setTouchEventsSet(true);
        }

        if (!swipeEnabled) {
          setSwipeEnabled(true);
          passedSwipeScrollTolerance = false;
        }
      };

      disableScroll();
    }
  }, [images, swipeEnabled, touchEventsSet]);

  const discountData = vdpGlobalData?.vehicleCardData;

  return (
    <>
      {customGalleriaItemTabs && customGalleriaItemTabs?.length > 0 && (
        <div className={styles.tabs}>
          {customGalleriaItemTabs?.map((item, index) => (
            <button
              key={index}
              name={"vdpTabButton"}
              aria-label={"vdp page content switcher"}
              className={cx(styles.tabsItem, {
                [styles.tabsItemActive]: currentTab === index,
              })}
              onClick={() => setCurrentTab(index)}
            >
              {item?.title}
            </button>
          ))}
          <button
            name={"vdpTabButton"}
            aria-label={"vdp page content switcher"}
            className={cx(styles.tabsItem, {
              [styles.tabsItemActive]: currentTab === customGalleriaItemTabs?.length,
            })}
            onClick={() => setCurrentTab(customGalleriaItemTabs?.length)}
          >
            {t_vdp("photos")}
          </button>
        </div>
      )}
      <div className={styles.galleryContainer}>
        {discountData?.specialText && (
          <Discount specialText={discountData.specialText} specialColor={discountData?.specialColor} />
        )}
        {!customGalleriaItemTabs?.length || currentTab === customGalleriaItemTabs?.length ? (
          images?.length > 1 ? (
            <Carousel
              className={`${styles.gallery} vehiclesPageCarousel`}
              showStatus={false}
              showIndicators={false}
              swipeable={swipeEnabled}
              thumbWidth={isMobile ? 72 : 96}
              selectedItem={selectedItem}
              preventMovementUntilSwipeScrollTolerance={true}
              swipeScrollTolerance={SCROLL_TOLLERANCE}
              renderArrowPrev={(clickHandler: () => void, hasPrev) =>
                hasPrev ? (
                  <div
                    className={cx(styles.galleryArrowContainer, styles.galleryArrowContainerLeft)}
                    onClick={clickHandler}
                  >
                    <CarCardImageButton
                      className={cx(styles.galleryArrow, styles.galleryArrowLeft)}
                      direction={ArrowDirection.LEFT}
                      onClick={clickHandler}
                    />
                  </div>
                ) : null
              }
              renderArrowNext={(clickHandler: () => void, hasNext) =>
                hasNext ? (
                  <div
                    className={cx(styles.galleryArrowContainer, styles.galleryArrowContainerRight)}
                    onClick={clickHandler}
                  >
                    <CarCardImageButton
                      className={cx(styles.galleryArrow, styles.galleryArrowRight)}
                      direction={ArrowDirection.RIGHT}
                      onClick={clickHandler}
                    />
                  </div>
                ) : null
              }
              onChange={(index: number) => setSelectedItem(index)}
              renderThumbs={() => {
                return isMobile
                  ? []
                  : smallImages?.length > 1
                    ? smallImages?.map((item, index) => (
                        <img
                          src={item || dealerPhoto || `/images/brands/${currentVehicle?.make?.toLowerCase()}.png`}
                          alt="Car"
                          loading={index === 0 ? undefined : "lazy"}
                          key={index}
                          onError={(e) => handleOnImageLoadError(e, currentVehicle)}
                        />
                      ))
                    : [];
              }}
            >
              {images.map((item, index) => (
                <img
                  src={item || dealerPhoto || `/images/brands/${currentVehicle?.make?.toLowerCase()}.png`}
                  alt="Car"
                  loading={index === 0 ? undefined : "lazy"}
                  key={index}
                  onError={(e) => handleOnImageLoadError(e, currentVehicle)}
                />
              ))}
            </Carousel>
          ) : (
            <div
              className={cx(styles.gallery, {
                [styles.galleryLogo]: !images?.length,
              })}
            >
              <img
                src={
                  images[0] ||
                  dealerPhoto ||
                  `/images/brands/${currentVehicle?.make?.toLowerCase()}.png` ||
                  "/images/out-of-stock.png"
                }
                alt="Car"
                onError={(e) => handleOnImageLoadError(e, currentVehicle)}
                loading={undefined}
              />
              {!images?.length && <span className={styles.noImageText}>{t_common("photos_coming_soon")}</span>}
            </div>
          )
        ) : (
          <div className={styles.iframe}>
            <Iframe url={customGalleriaItemTabs[currentTab]?.iframeUrl || ""} />
          </div>
        )}
      </div>
    </>
  );
};

export default Gallery;
